import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ThroughlinePulseChart } from "@/components/throughline/ThroughlinePulseChart";
import type { CoachThroughlineSelectedDayViewModel } from "@/lib/behavior/viewModels/buildCoachThroughlineSelectedDay";
import type { ThroughlineArcPoint, ThroughlineGapAlert, ThroughlineNodeState } from "@/lib/throughlineAdapter";
import {
  THROUGHLINE_STATE_BADGE_STYLES,
  THROUGHLINE_WINDOW_DEFAULT_COPY,
  THROUGHLINE_WINDOW_OPTIONS,
  type ThroughlineWindowDays,
} from "@/lib/throughlinePresentationConfig";

type CoachThroughlineSectionProps = {
  points: ThroughlineArcPoint[];
  chartLabelPoints: ThroughlineArcPoint[];
  selectedDayKey: string | null;
  selectedDayViewModel: CoachThroughlineSelectedDayViewModel | null;
  windowDays: ThroughlineWindowDays;
  stateCounts: Record<ThroughlineNodeState, number>;
  gapAlert: ThroughlineGapAlert;
  sparseWindowAutoExpanded: boolean;
  onSelectDay: (dayKey: string) => void;
  onSelectWindowDays: (days: ThroughlineWindowDays) => void;
  onOpenAllSources: () => void;
  onAskVeeraAboutDay: () => void;
};

export function CoachThroughlineSection({
  points,
  chartLabelPoints,
  selectedDayKey,
  selectedDayViewModel,
  windowDays,
  stateCounts,
  gapAlert,
  sparseWindowAutoExpanded,
  onSelectDay,
  onSelectWindowDays,
  onOpenAllSources,
  onAskVeeraAboutDay,
}: CoachThroughlineSectionProps) {
  const selectedStateBadgeClassName =
    selectedDayViewModel?.state === "healthy"
      ? "bg-emerald-100 text-emerald-700 border-emerald-400/40 dark:bg-emerald-500/15 dark:text-emerald-300"
      : selectedDayViewModel?.state === "stalled"
        ? "bg-amber-100 text-amber-700 border-amber-400/40 dark:bg-amber-500/15 dark:text-amber-300"
        : "bg-rose-100 text-rose-700 border-rose-400/40 dark:bg-rose-500/15 dark:text-rose-300";

  return (
    <Card className="border-violet-300/40 bg-gradient-to-br from-violet-50 to-purple-50 shadow-sm dark:border-violet-400/30 dark:from-[#1a1230] dark:via-[#140e25] dark:to-[#0f0a1d] dark:shadow-[0_0_60px_rgba(167,139,250,0.12)]">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-h3 text-foreground dark:text-violet-100">Coach Throughline</CardTitle>
            <p className="text-sm text-muted-foreground dark:text-violet-200/70">
              How the pattern has been moving across recent days.
            </p>
            <p className="text-xs text-muted-foreground dark:text-violet-300/70">
              {THROUGHLINE_WINDOW_DEFAULT_COPY}
              {sparseWindowAutoExpanded ? " Low recent activity detected, so the chart is currently showing 30 days." : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {THROUGHLINE_WINDOW_OPTIONS.map((days) => (
              <Button
                key={days}
                type="button"
                size="sm"
                variant={windowDays === days ? "default" : "outline"}
                className={windowDays === days ? "bg-violet-500 text-white hover:bg-violet-400" : "border-violet-300/40 text-violet-700 hover:bg-violet-100 dark:text-violet-200 dark:hover:bg-violet-400/10"}
                onClick={() => onSelectWindowDays(days)}
              >
                {days}d
              </Button>
            ))}
            {(Object.keys(THROUGHLINE_STATE_BADGE_STYLES) as ThroughlineNodeState[]).map((stateKey) => (
              <Badge key={stateKey} className={THROUGHLINE_STATE_BADGE_STYLES[stateKey]}>
                {stateKey === "healthy" ? "On Track" : stateKey === "stalled" ? "Needs Consistency" : "Needs Attention"} {stateCounts[stateKey]}
              </Badge>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6 pt-2 space-y-4">
        {gapAlert.active ? (
          <div className="rounded-lg border border-amber-400/35 bg-amber-500/10 p-3">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-100">Recent attention pattern</p>
            <p className="text-xs text-amber-700 mt-1 dark:text-amber-200/90">
              {gapAlert.gapDays} alignment-gap days and {gapAlert.lowEvidenceDays} low-evidence days surfaced in the last {gapAlert.recentWindowSize} days.
            </p>
          </div>
        ) : null}
        {points.length === 0 ? (
          <div className="rounded-lg border border-violet-200/40 bg-violet-50 p-4 dark:border-violet-300/20 dark:bg-black/20">
            <p className="text-sm text-foreground dark:text-violet-100">No throughline snapshot yet.</p>
            <p className="text-sm text-muted-foreground dark:text-violet-200/70">
              Upload transcripts and reflections to unlock day-level coaching decisions.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <ThroughlinePulseChart
              points={points}
              chartLabelPoints={chartLabelPoints}
              selectedDayKey={selectedDayKey}
              onSelectDay={onSelectDay}
            />
            {selectedDayViewModel ? (
              <div className="rounded-lg border border-violet-200/40 bg-violet-50/60 p-4 space-y-4 dark:border-violet-300/30 dark:bg-black/25">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground dark:text-violet-300/80">Selected day</p>
                    <p className="text-sm font-medium text-foreground dark:text-violet-50">{selectedDayViewModel.heading}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={selectedStateBadgeClassName}>
                      {selectedDayViewModel.stateLabel}
                    </Badge>
                    <Badge variant="outline" className="border-violet-300/40 text-violet-700 dark:text-violet-200">
                      {selectedDayViewModel.confidenceLabel}
                    </Badge>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-violet-300/40 text-violet-700 hover:bg-violet-100 dark:text-violet-200 dark:hover:bg-violet-400/10"
                      onClick={onOpenAllSources}
                    >
                      Open sources
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-violet-300/40 text-violet-700 hover:bg-violet-100 dark:text-violet-200 dark:hover:bg-violet-400/10"
                      onClick={onAskVeeraAboutDay}
                    >
                      Ask Veera about this day
                    </Button>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-foreground/90 dark:text-violet-100/90">{selectedDayViewModel.summary}</p>
                  <p className="text-xs text-muted-foreground dark:text-violet-300/85">{selectedDayViewModel.confidenceReason}</p>
                </div>
                {selectedDayViewModel.coachAttention ? (
                  <div className="rounded-md border border-amber-300/35 bg-amber-500/10 px-3 py-2">
                    <p className="text-xs text-amber-800 dark:text-amber-200">{selectedDayViewModel.coachAttention}</p>
                  </div>
                ) : null}
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-md border border-violet-200/40 bg-white/70 p-3 space-y-2 dark:border-violet-300/20 dark:bg-black/20">
                    <p className="text-xs uppercase tracking-wide text-violet-600 dark:text-violet-200/80">What stood out</p>
                    {selectedDayViewModel.saidSignals.map((signal, index) => (
                      <p key={`said-${index}`} className="text-xs text-foreground/80 dark:text-violet-100/90">
                        {signal}
                      </p>
                    ))}
                  </div>
                  <div className="rounded-md border border-violet-200/40 bg-white/70 p-3 space-y-2 dark:border-violet-300/20 dark:bg-black/20">
                    <p className="text-xs uppercase tracking-wide text-violet-600 dark:text-violet-200/80">What may be missing</p>
                    {selectedDayViewModel.unsaidSignals.map((signal, index) => (
                      <p key={`unsaid-${index}`} className="text-xs text-foreground/80 dark:text-violet-100/90">
                        {signal}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
